import { useState } from "react";
import { ADMIN_LOGIN, adminOrgPath, tenantPath } from "../../lib/paths.js";
import { useAtomValue, useSetAtom } from "jotai";
import { Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import { ApiError, type LoginResponse } from "../../lib/api-types.js";
import { authAtom, clearAuthAtom, loginAtom } from "../../lib/auth-store.js";
import { Spinner } from "../../components/Spinner.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";

/**
 * 企业专属后台入口（2026-07-04 入口分离）：`/:orgCode` = 该企业自己的管理后台。
 *  - 未登录 → 就地渲染该企业的登录页（组织码来自 URL 不用填），登录成功后
 *    auth 态翻转、当前 URL 原地进入后台（深链天然保留）
 *  - 已登录 org-admin 但 orgCode 不符 → 弹回自己企业的入口
 *  - 已登录平台身份（super/support）→ 走平台面 /organizations/:code
 * 平台超管入口 = /login（两入口自此分离，互不出现对方形态）。
 */
export function TenantLayout(): JSX.Element {
  const { code } = useParams<{ code: string }>();
  const auth = useAtomValue(authAtom);
  const clearAuth = useSetAtom(clearAuthAtom);
  const navigate = useNavigate();

  if (!code) return <Navigate to={ADMIN_LOGIN} replace />;

  if (!auth) return <TenantLogin orgCode={code} />;

  if (auth.scope !== "org-admin") {
    return <Navigate to={adminOrgPath(code)} replace />;
  }
  if (auth.orgCode !== code) {
    // orgCode 缺失（异常态）→ 平台登录；否则弹回自己组织入口
    return auth.orgCode ? (
      <Navigate to={tenantPath(auth.orgCode)} replace />
    ) : (
      <Navigate to={ADMIN_LOGIN} replace />
    );
  }

  const handleLogout = (): void => {
    clearAuth();
    navigate(tenantPath(code), { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-dosia-bg text-dosia-text">
      <header className="h-14 shrink-0 border-b border-dosia-border bg-dosia-bg-light flex items-center justify-between px-4">
        <div>
          <span className="text-base font-semibold">DOSIA 企业管理后台</span>
          <span className="ml-2 text-xs text-dosia-text-secondary font-mono">{code}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-dosia-text-secondary">企业管理员</span>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded border border-dosia-border px-2.5 py-1 text-xs text-dosia-text-secondary hover:bg-dosia-bg-lighter"
          >
            退出
          </button>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-6 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

function TenantLogin({ orgCode }: { orgCode: string }): JSX.Element {
  const setLogin = useSetAtom(loginAtom);
  const [credential, setCredential] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiCall<LoginResponse>("POST", "/api/admin/org-login", {
        body: { orgCode, credential: credential.trim(), password },
      });
      // 写 auth 后本组件重渲染进后台，URL 不动（深链保留）
      setLogin({
        token: res.token,
        scope: res.scope,
        orgCode: res.orgCode,
        expiresAt: res.expiresAt,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError("用户名或密码错误（请确认使用贵司 DOSIA 企业账号）");
        } else if (err.status === 403 && err.code === "not-org-admin") {
          setError("该账号不是企业管理员，请联系贵司管理员");
        } else if (err.status === 400) {
          setError("请完整填写登录信息");
        } else {
          setError(err.message || `登录失败（${err.code}）`);
        }
      } else {
        setError("网络异常，请稍后再试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded border border-dosia-border bg-dosia-bg-input px-3 py-1.5 text-sm text-dosia-text focus:border-dosia-accent focus:outline-none";

  return (
    <div className="min-h-screen flex items-center justify-center bg-dosia-bg px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-dosia-border bg-dosia-bg-light p-6 shadow-sm"
      >
        <div className="space-y-1">
          <h1 className="text-lg font-semibold text-dosia-text">
            DOSIA 企业管理后台
          </h1>
          <p className="text-sm text-dosia-text-secondary">
            组织 <span className="font-mono">{orgCode}</span> · 管理员登录
          </p>
        </div>

        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

        <label className="block space-y-1">
          <span className="text-sm text-dosia-text-secondary">手机号 / 用户名</span>
          <input
            type="text"
            value={credential}
            onChange={(e) => setCredential(e.target.value)}
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            required
            className={inputClass}
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm text-dosia-text-secondary">密码</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className={inputClass}
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 rounded bg-dosia-accent px-4 py-2 text-sm font-medium text-dosia-bg-light hover:bg-dosia-accent-hover disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? <Spinner size="sm" className="text-dosia-bg-light" /> : null}
          {submitting ? "登录中…" : "登录"}
        </button>

        <p className="text-xs text-dosia-text-dim">
          使用与 DOSIA 桌面端相同的企业账号登录，需管理员角色。
        </p>
      </form>
    </div>
  );
}
